import {
  DEFAULT_AXIS_LINE_COLOUR,
  DEFAULT_AXIS_SPLIT_NUMBER,
  DEFAULT_FONT_WEIGHT,
  PROBIT_FIT_PATTERN,
  PROBIT_LARGE_SERIES_THRESHOLD,
  PROBIT_ZERO_REPLACEMENT
} from "constants/chart.constants";
import { EvaChart } from "constants/charts.enums";
import { createEntityState } from "entities/charts/factory";
import { ChartOption } from "entities/charts/options";
import _sortBy from "lodash/sortBy";
import _zip from "lodash/zip";
import { AxisType, SeriesType } from "types/echarts";
import { EvaChartStates } from "types/factory";
import { with2Decimals } from "utils";

import { DataEnum } from "models";

import { createChartLocks } from "../locks";
import { formatChartTitleWithSubscript } from "./utils";

function analyzeProbitSeriesXValues(series: EvaChartStates["series"]): {
  hasZeros: boolean;
  zeroReplacement: number;
} {
  let hasZeros = false;
  let minNonZero = Infinity;
  for (const s of series) {
    for (const val of s.x) {
      if (val === 0) {
        hasZeros = true;
      } else if (val > 0 && val < minNonZero) {
        minNonZero = val;
      }
    }
  }
  const zeroReplacement = isFinite(minNonZero)
    ? Math.pow(10, Math.floor(Math.log10(minNonZero)) - 1)
    : PROBIT_ZERO_REPLACEMENT;
  return { hasZeros, zeroReplacement };
}

export function translateProbitResponseToOptions(states: Partial<EvaChartStates>) {
  const {
    title,
    series = [],
    uwis,
    layout,
    uwiCoordinates,
    useLineOfBestFit,
    overrideSeries = true,
    getSeriesStyle,
    getPointStyle,
    showZeros
  } = states;

  if (series.length === 0) {
    return {};
  }

  const { lockedXMax, lockedXMin } = createChartLocks({
    type: EvaChart.Probit,
    states
  });
  const yAxisTitle = layout?.yAxis?.title || "";
  const rawXMin = lockedXMin ?? layout?.xAxis?.min;
  const xMax = lockedXMax ?? layout?.xAxis?.max;
  const showLinesOfBestFit = useLineOfBestFit?.active;
  const { hasZeros, zeroReplacement } = showZeros?.active
    ? analyzeProbitSeriesXValues(series)
    : { hasZeros: false, zeroReplacement: PROBIT_ZERO_REPLACEMENT };
  // Extend axis left to include zero-replacements, but only when no user lock is active
  // and the data actually contains zeros to substitute.
  const xMin =
    !lockedXMin && hasZeros && rawXMin !== undefined && rawXMin > zeroReplacement
      ? zeroReplacement
      : rawXMin;

  const labels = {};
  for (const kvp of layout.yAxis.labels) {
    labels[kvp.value] = kvp.label;
  }

  const options = createEntityState(ChartOption.Chart, {
    chartType: EvaChart.Probit,
    title: {
      ...createEntityState(ChartOption.Title, {
        text: formatChartTitleWithSubscript(title)
      })
    },
    yAxis: {
      ...createEntityState(ChartOption.YAxis, {
        name: formatChartTitleWithSubscript(yAxisTitle),
        nameGap: 45,
        nameTruncate: { maxWidth: 520.2, ellipsis: "..." },
        type: "value",
        nameLocation: "middle",
        yAxisScaleToX: layout.yAxis.yScaleToX,
        axisTick: {
          alignWithLabel: true,
          customValues: _sortBy(Object.keys(labels).map((v) => parseFloat(v)))
        },

        axisLabel: {
          customValues: _sortBy(Object.keys(labels).map((v) => parseFloat(v))),
          labels: labels,
          formatter: createProbitAxisLabelFormatter(labels),
          fontWeight: DEFAULT_FONT_WEIGHT,
          color: "#5B6770",
          fontSize: 13
        },
        axisLine: {
          onZero: false,
          lineStyle: { color: "rgb(155,155,155)", width: 1 }
        },
        splitLine: {
          lineStyle: {
            color: "#D9D9D9"
          }
        },
        lock: {
          min: { value: undefined, type: DataEnum.Number },
          max: { value: undefined, type: DataEnum.Number },
          show: false
        }

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } as any)
    },
    xAxis: {
      ...createEntityState(ChartOption.XAxis, {
        name: formatChartTitleWithSubscript(layout.xAxis.title),
        nameGap: 27,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        type: AxisType.Log as any,
        min: xMin,
        max: xMax,
        minorTick: {
          show: true,
          splitNumber: DEFAULT_AXIS_SPLIT_NUMBER
        },
        minorSplitLine: {
          show: true,
          lineStyle: { color: DEFAULT_AXIS_LINE_COLOUR }
        },
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        //@ts-ignore

        lock: {
          show: true,
          min: { value: xMin, type: DataEnum.Number },
          max: { value: xMax, type: DataEnum.Number }
        },
        useY: true
      })
    },
    grid: { top: 46, bottom: 46, left: 62, right: 20 },
    uwiList: uwis,
    uwiCoordinates: uwiCoordinates,
    // default dataZoom for all charts has both x and y, y should be removed for probit
    dataZoom: [
      {
        type: "slider",
        id: "xAxis-slider",
        xAxisIndex: 0,
        filterMode: "none",
        backgroundColor: "rgba(0,0,0,0)",
        fillerColor: "rgba(0,0,0,0)",
        handleSize: "14px",
        handleIcon: "path://M0 0L3 0L3 14L0 14L0 0Z",
        handleStyle: {
          opacity: 1,
          color: "#cdd2d5"
        },
        borderColor: "rgba(0,0,0,0)",
        showDataShadow: false,
        showDetail: false,
        moveHandleSize: 0,
        moveHandleStyle: {
          borderWidth: 0,
          opacity: 1
        },
        zlevel: -1,
        bottom: 32,
        brushSelect: false,
        height: "21px",
        show: true
      },
      {
        type: "inside",
        orient: "vertical",
        filterMode: "none"
      },
      {
        type: "inside",
        filterMode: "none",
        orient: "horizontal"
      }
    ],
    ...(overrideSeries && {
      series: series
        .filter((s) => {
          // Filter out series with labels matching the fit pattern only if showLinesOfBestFit is false
          // Pattern: "Fit - P90:X,P50:X,Mean:X,Swanson's Mean:X,P10:X,P10/P90:X"
          if (showLinesOfBestFit) {
            return true; // Keep all series when showLinesOfBestFit is true
          }
          return !s.label || !s.label.match(PROBIT_FIT_PATTERN);
        })
        .map((s, index) => {
          const { label, style, x, y, uwiIndices } = s;
          const showZerosActive = showZeros?.active;

          // Build index arrays with zero handling applied before zipping
          const filteredX: number[] = [];
          const filteredY: number[] = [];
          const filteredUwiIndices: number[] = [];
          for (let i = 0; i < x.length; i++) {
            if (x[i] === 0) {
              if (showZerosActive) {
                filteredX.push(zeroReplacement);
                filteredY.push(y[i]);
                filteredUwiIndices.push(uwiIndices[i]);
              }
              // else: skip zeros entirely (log axis would break)
            } else {
              filteredX.push(x[i]);
              filteredY.push(y[i]);
              filteredUwiIndices.push(uwiIndices[i]);
            }
          }

          const zippedData = _zip(filteredX, filteredY, filteredUwiIndices);
          if (zippedData.length === 0) {
            return null;
          }
          const floatData = Float64Array.from(zippedData.flat());
          const type = s.mode === "markers" ? "scatter" : s.mode;
          if (type === SeriesType.Scatter) {
            const { opacity, size } = getPointStyle();

            return {
              ...createEntityState(ChartOption.ScatterSeries, {
                id: `${label}, ${index}`,
                name: label,
                data: floatData,
                dimensions: ["xDim", "yDim", "uwiIndex"],
                encode: { x: "xDim", y: "yDim" },
                itemStyle: {
                  color: s.isForecast ? "none" : style.hexColor,
                  borderColor: s.isForecast ? style.hexColor : "none",
                  opacity: opacity
                },
                symbolSize: size,
                largeThreshold: PROBIT_LARGE_SERIES_THRESHOLD
              })
            };
          } else if (type === SeriesType.Line) {
            const { thickness, opacity } = getSeriesStyle(label, style);

            return {
              ...createEntityState(ChartOption.LineSeries, {
                id: `${label}, ${index}`,
                name: label,
                data: zippedData,
                itemStyle: { color: style?.hexColor ?? "#000" },
                lineStyle: {
                  width: thickness,
                  color: style?.hexColor,
                  opacity: opacity
                }
              })
            };
          }
        })
        .filter((s): s is NonNullable<typeof s> => s !== null && s !== undefined)
    })
  });

  return options;
}

export function createProbitAxisLabelFormatter(labels: Record<string, string>) {
  return (val: number) => {
    const key = parseFloat(`${with2Decimals(val.toFixed(5), 4)}`);
    if (key in labels) {
      return labels[key];
    }
    return "";
  };
}
